import {Component, forwardRef, OnInit} from '@angular/core';
import {
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import {CommonModule} from '@angular/common';
import {densities, sd_bit_profiles, sd_pm_modes} from '../constants';

/**
 * Stardust settings for the `vod-default-passthrough-sffw` template.
 *
 * Unlike the VOD Default form there is no "watermark on/off" switch and no encryption or thumbnail
 * option: passthrough delivery *always* watermarks (the template rejects a job without the Stardust
 * keys) and does no DRM or CMAF packaging at all. Everything else about the file -- codec, container,
 * resolution, audio and subtitle streams -- is passed through untouched.
 */
@Component({
  selector: 'app-vod-passthrough-format-specific',
  templateUrl: './vod-passthrough-format-specific.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => VodPassthroughFormatSpecificComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: VodPassthroughFormatSpecificComponent,
      multi: true
    }
  ],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
  ]
})
export class VodPassthroughFormatSpecificComponent implements OnInit, ControlValueAccessor {
  /**
   * The seven keys `VtkTemplateVodPassthrough.STARDUST_REQUIRED_KEYS` insists on. A missing one fails
   * the job at render time ("Missing '<key>' in format_specific_data"), so none of these may be
   * dropped from the emitted value -- which is why `sd_seed` is a plain field here rather than the
   * auto-toggle the VOD Default form uses (a disabled control is absent from `form.value`).
   */
  static DEFAULTS = {
    sd_bit_profile: sd_bit_profiles[1].id,
    sd_wm_id: Math.floor(Math.random() * (sd_bit_profiles[1].max + 1)),
    sd_strength: 4,
    sd_pixel_density: densities[5].id,
    sd_superpixel_density: densities[5].id,
    sd_pm_mode: sd_pm_modes[2].id,
    sd_seed: -1,
  };

  /** The only keys the template accepts; everything else is dropped on the way to the API. */
  private static KEYS = [
    'sd_bit_profile', 'sd_wm_id', 'sd_strength',
    'sd_pixel_density', 'sd_superpixel_density', 'sd_pm_mode', 'sd_seed',
  ];

  /**
   * Reduces a format_specific_data object to exactly the keys this workflow needs.
   *
   * Applied at submit time because the outer control is not necessarily something this component
   * emitted -- a restored form state is written into the FormArray directly and `writeValue` patches
   * without emitting, so a stored object of a different shape would otherwise be sent verbatim.
   */
  static forSubmit(value: any): any {
    const src = value ?? {};
    return VodPassthroughFormatSpecificComponent.KEYS.reduce((acc, key) => {
      acc[key] = src[key] ?? VodPassthroughFormatSpecificComponent.DEFAULTS[key];
      return acc;
    }, {} as any);
  }

  form: FormGroup;
  densities = densities;
  sd_bit_profiles = sd_bit_profiles;
  sd_pm_modes = sd_pm_modes;

  changed: any = () => {};
  touched: any = () => {};

  constructor(private formBuilder: FormBuilder) {
    this.form = this.formBuilder.group({
      sd_bit_profile: [VodPassthroughFormatSpecificComponent.DEFAULTS.sd_bit_profile, [Validators.required]],
      sd_wm_id: [
        VodPassthroughFormatSpecificComponent.DEFAULTS.sd_wm_id,
        [Validators.required, Validators.min(0), Validators.max(sd_bit_profiles[1].max)]
      ],
      sd_strength: [4, [Validators.required, Validators.min(1), Validators.max(20)]],
      sd_pixel_density: [densities[5].id, [Validators.required]],
      sd_superpixel_density: [densities[5].id, [Validators.required]],
      sd_pm_mode: [sd_pm_modes[2].id, [Validators.required]],
      sd_seed: [-1, [Validators.required, Validators.min(-1), Validators.max(31)]],
    });

    this.form.valueChanges.subscribe(() => {
      this.touched();
      // The ID's ceiling follows the chosen bit profile, so re-validate whenever either moves.
      const profile = sd_bit_profiles.find(p => p.id === this.form.controls['sd_bit_profile'].value);
      this.form.controls['sd_wm_id'].setValidators(
        [Validators.required, Validators.min(0), Validators.max(profile ? profile.max : sd_bit_profiles[1].max)]
      );
      this.form.controls['sd_wm_id'].updateValueAndValidity({emitEvent: false});
      this.changed(this.form.value);
    });
  }

  ngOnInit(): void {
    this.changed(this.form.value);
  }

  writeValue(value: any): void {
    if (value) {
      this.form.patchValue(value, {emitEvent: false});
    }
  }

  registerOnChange(fn: any): void {
    this.changed = fn;
  }

  registerOnTouched(fn: any): void {
    this.touched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    isDisabled ? this.form.disable() : this.form.enable();
  }

  validate({value}: FormControl) {
    return this.form.valid ? null : {invalid: true};
  }
}
